import React, { useState, useCallback, useRef, useContext, createContext } from 'react';
import s from './ui-toast.module.scss';

export type ToastVariant = 'success' | 'info' | 'warning' | 'danger';

export interface UIToastProps {
    variant: ToastVariant;
    title: string;
    message?: string;
    actions?: React.ReactNode;
    onClose?: () => void;
    className?: string;
}

export interface ToastOptions {
    variant: ToastVariant;
    title: string;
    message?: string;
    actions?: React.ReactNode;
    duration?: number;
}

interface ToastEntry extends ToastOptions {
    id: number;
    exiting: boolean;
}

/* ── Icons per variant ──────────────────────────────── */

const CheckIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <path d="M4.5 10l4 4 7-8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const InfoIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <path d="M10 9v6M10 6v.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

const WarningIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <path d="M10 6v5M10 13.5v.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

const DangerIcon: React.FC = () => (
    <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
        <path d="M4 4l10 10M14 4L4 14" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

const CloseIcon: React.FC = () => (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
        <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const variantIcon: Record<ToastVariant, React.FC> = {
    success: CheckIcon,
    info: InfoIcon,
    warning: WarningIcon,
    danger: DangerIcon,
};

const UIToast: React.FC<UIToastProps> = ({
    variant,
    title,
    message,
    actions,
    onClose,
    className,
}) => {
    const Icon = variantIcon[variant];
    const cls = [s.toast, s[variant], className ?? ''].filter(Boolean).join(' ');

    return (
        <div className={cls} role="alert">
            <div className={s.iconRing}>
                <div className={s.iconCircle}>
                    <Icon />
                </div>
            </div>

            <div className={s.body}>
                <div className={s.title}>{title}</div>
                {message && <div className={s.message}>{message}</div>}
                {actions && (
                    <div className={s.actions}>{actions}</div>
                )}
            </div>

            {onClose && (
                <button type="button" className={s.close} onClick={onClose} aria-label="Dismiss">
                    <CloseIcon />
                </button>
            )}
        </div>
    );
};

export default UIToast;

/* ── Toast context + provider ────────────────────── */

type ShowToast = (opts: ToastOptions) => void;

const ToastContext = createContext<ShowToast>(() => {});

export const useToast = (): ShowToast => useContext(ToastContext);

let nextId = 0;

export const UIToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastEntry[]>([]);
    const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    const dismiss = useCallback((id: number) => {
        // Start exit animation
        setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
        // Remove after animation
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 220);
        // Clear auto-dismiss timer
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }, []);

    const show: ShowToast = useCallback((opts) => {
        const id = ++nextId;
        const duration = opts.duration ?? 3500;
        setToasts((prev) => [...prev, { ...opts, id, exiting: false }]);
        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
    }, [dismiss]);

    return (
        <ToastContext.Provider value={show}>
            {children}
            <div className={s.container}>
                {toasts.map((t) => {
                    const animCls = t.exiting ? s.exiting : s.entering;
                    return (
                        <UIToast
                            key={t.id}
                            variant={t.variant}
                            title={t.title}
                            message={t.message}
                            actions={t.actions}
                            onClose={() => dismiss(t.id)}
                            className={animCls}
                        />
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
};
